import { Box, Paper, Stack, Typography } from "@mui/material";
import { JobProgress } from "./JobProgress";
import Reschedule from "../Reschedule";
import PerformPm from "../PerformPm";

const normalizeJobStatus = (status) => (status || "").trim().toLowerCase();

const WORKFLOW_STEPS = [
  { label: "Scheduled", match: ["not started", "scheduled"] },
  { label: "In transit", match: ["in transit"] },
  { label: "Working", match: ["in progress"] },
  { label: "Done", match: ["completed"] },
];

function getActiveStepIndex(status) {
  const normalized = normalizeJobStatus(status);
  const index = WORKFLOW_STEPS.findIndex((step) =>
    step.match.includes(normalized)
  );
  return index >= 0 ? index : 0;
}

function JobStatusSteps({ status }) {
  const normalized = normalizeJobStatus(status);
  if (normalized === "cancelled" || normalized === "reschedule") {
    return null;
  }

  const activeIndex = getActiveStepIndex(status);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        mb: 2,
        px: 0.25,
      }}
      aria-label="Job progress"
    >
      {WORKFLOW_STEPS.map((step, index) => {
        const isActive = index === activeIndex;
        const isComplete = index < activeIndex;

        return (
          <Box
            key={step.label}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
            }}
          >
            {index > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: 14,
                  right: "50%",
                  width: "100%",
                  height: 2,
                  bgcolor: isComplete || isActive ? "persiangreen.main" : "grey.300",
                  zIndex: 0,
                }}
              />
            )}
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.75rem",
                fontWeight: 700,
                zIndex: 1,
                bgcolor: isComplete || isActive ? "persiangreen.main" : "grey.200",
                color: isComplete || isActive ? "common.white" : "text.secondary",
              }}
            >
              {isComplete ? "✓" : index + 1}
            </Box>
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                textAlign: "center",
                fontSize: "0.65rem",
                lineHeight: 1.2,
                color: isActive ? "persiangreen.main" : "text.secondary",
                fontWeight: isActive ? 700 : 400,
              }}
            >
              {step.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

const primaryButtonSx = {
  minHeight: 48,
  fontSize: "1rem",
  fontWeight: 600,
  textTransform: "none",
};

const secondaryButtonSx = {
  minHeight: 44,
  flex: 1,
  fontSize: { xs: "0.8125rem", sm: "0.875rem" },
  fontWeight: 600,
  textTransform: "none",
};

export function JobActionsBar({ job }) {
  const status = normalizeJobStatus(job?.status);
  const isTerminal =
    status === "completed" || status === "cancelled" || status === "reschedule";
  const showSecondary = !isTerminal;

  return (
    <Paper
      elevation={0}
      className="job-actions-bar"
      sx={{
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 2,
        p: { xs: 1.5, sm: 2 },
        mb: 2,
        bgcolor: "grey.50",
      }}
    >
      {!isTerminal && <JobStatusSteps status={job?.status} />}

      <Box sx={{ mb: showSecondary ? 1.5 : 0 }}>
        <JobProgress
          jobStatus={job?.status}
          job={job}
          primaryButtonSx={primaryButtonSx}
        />
      </Box>

      {showSecondary && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            "& > .job-action-secondary": {
              flex: 1,
              minWidth: 0,
              display: "flex",
            },
          }}
        >
          <Box className="job-action-secondary">
            <Reschedule
              call={job}
              buttonLabel="Reschedule"
              buttonVariant="outlined"
              buttonSx={secondaryButtonSx}
            />
          </Box>
          <Box className="job-action-secondary">
            <PerformPm
              job={job}
              triggerVariant="outlined"
              triggerSx={secondaryButtonSx}
            />
          </Box>
        </Stack>
      )}
    </Paper>
  );
}
